import { getSignInUrl } from '@ankr.com/auth';
import { useCallback, useMemo } from 'react';

import { getUrlWithoutTelegramSecret } from 'modules/oauth/utils/getUrlWithoutTelegramSecret';

import { AUTH_APP_ID, AUTH_APP_PATH, AUTH_PATH } from '../const';
import { useAuthInstance } from './useAuthInstance';

export interface UseSSOAuthProps {
  hasAPIKeysNotification?: boolean;
  referralCode?: string;
}

export const useSSOAuth = ({
  hasAPIKeysNotification,
  referralCode,
}: UseSSOAuthProps = {}) => {
  const { isSignedIn } = useAuthInstance();

  const buildSignInUrl = useCallback(
    () =>
      getSignInUrl({
        authPath: AUTH_PATH,
        appPath: AUTH_APP_PATH,
        appId: AUTH_APP_ID,
        referralCode,
        hasReferralCodeBox: true,
        hasClickwrapAgreement: true,
        hasAPIKeysNotification,
        // other query params (invitation token, confirmation data) must
        // survive the login round-trip, but the telegram oauth secret is
        // consumed in-app only and must not be sent to the auth origin.
        // read window.location lazily so the referrer reflects the URL at
        // sign-in time rather than a stale value from when this hook first ran
        // (SignInButton lives in the persistent header and never remounts)
        referrer: getUrlWithoutTelegramSecret(window.location.href),
      }),
    [hasAPIKeysNotification, referralCode],
  );

  // for rendering as an href; the actual navigation rebuilds the URL at click
  // time via handleSignIn so its referrer is always current
  const signInUrl = useMemo(() => buildSignInUrl(), [buildSignInUrl]);

  const handleSignIn = () => {
    window.location.assign(buildSignInUrl());
  };

  return { handleSignIn, isSignedIn, signInUrl };
};
